import React, { useMemo } from 'react';
import { Box } from '../Box';
import { ArrowDropDownIcon, InfoIcon } from '../Icons';
import { Tooltip } from '../Tooltip';
import Typography from '../Typography';
import { BarGradientLabel } from './BarGradientLabel';

export interface PositionedLabelProps {
    label: string | number;
    /**
     * position of the label as a percent
     * ex. position: 100 = 100%
     */
    position: number;
    tooltip?: string;
}
export interface BarGradientProps {
    value?: number;
    valueText?: string;
    /** percent (0-100) used to position the value's arrow on the gradient */
    valuePosition?: number;
    min?: number;
    /** Defaults to `min` if minLabel is omitted */
    minLabel?: string;
    minTooltip?: string;
    max?: number;
    /** Defaults to `max` if maxLabel is omitted */
    maxLabel?: string;
    maxTooltip?: string;
    /** Overrides minLabel & maxLabel. */
    positionedLabels?: PositionedLabelProps[];
    colors?: string[];
    gradientString?: string;
    height?: string;
    marginTop?: string;
    showValueLabel?: boolean;
    /**
     * Percent of the total scale that each corresponding anchor is associated with
     * Omitting `percentages` and/or `anchors` will cause values to be positioned linearly.
     */
    percentages?: number[];
}

const ARROW_SIZE = 32;

const getArrowCSSPosition = (percentPosition: number) => {
    return `calc(${percentPosition}% - ${ARROW_SIZE / 2}px)`;
};

const BarGradient = ({
    value,
    valueText,
    valuePosition,
    min,
    minLabel,
    minTooltip,
    max,
    maxLabel,
    maxTooltip,
    positionedLabels,
    gradientString,
    height,
    marginTop = '24px',
    showValueLabel = true
}: BarGradientProps) => {
    const hasData = value !== undefined && !isNaN(value) && min !== undefined && max !== undefined;
    const arrowPosition = useMemo(() => {
        if (hasData && typeof valuePosition === 'number') {
            return getArrowCSSPosition(valuePosition);
        }
        return undefined;
    }, [hasData, value, valuePosition]);

    const DefaultLabels = (
        <Box
            sx={{
                display: 'flex',
                justifyContent: 'space-between'
            }}
        >
            <Box sx={{ display: 'flex' }}>
                <Typography variant="body2">{minLabel || min}</Typography>
                {minTooltip && (
                    <Tooltip title={minTooltip}>
                        <InfoIcon
                            sx={{
                                fontSize: '14px',
                                marginLeft: '2px'
                            }}
                        />
                    </Tooltip>
                )}
            </Box>
            <Box sx={{ display: 'flex' }}>
                <Typography variant="body2">{maxLabel || max}</Typography>
                {maxTooltip && (
                    <Tooltip title={maxTooltip}>
                        <InfoIcon
                            sx={{
                                fontSize: '14px',
                                marginLeft: '2px'
                            }}
                        />
                    </Tooltip>
                )}
            </Box>
        </Box>
    );

    const PositionedLabels = positionedLabels && positionedLabels.length && (
        <Box width="100%" height="1rem" position="relative">
            {positionedLabels.map(({ label, position, tooltip }, idx) => {
                return (
                    <Box
                        key={idx}
                        sx={{
                            display: 'flex',
                            position: 'absolute',
                            left: `${position}%`,
                            transform: 'translate(-50%)'
                        }}
                    >
                        <Typography variant="body2">{label}</Typography>
                        {tooltip && (
                            <Tooltip title={tooltip}>
                                <InfoIcon
                                    sx={{
                                        fontSize: '14px',
                                        marginLeft: '2px'
                                    }}
                                />
                            </Tooltip>
                        )}
                    </Box>
                );
            })}
        </Box>
    );
    return (
        <Box width="100%">
            {showValueLabel && (valueText || value) && <BarGradientLabel label={valueText || `${value}`} />}
            {/* indicator arrow */}
            {hasData && (
                <ArrowDropDownIcon
                    data-testid="bar-gradient-arrow"
                    sx={{
                        fontSize: `${ARROW_SIZE}px`,
                        marginLeft: arrowPosition,
                        marginBottom: `-${ARROW_SIZE / 2}px`
                    }}
                />
            )}
            {/* color gradient */}
            <Box
                data-testid="bar-color-gradient"
                sx={{
                    height: height || '20px',
                    width: '100%',
                    marginBottom: '4px',
                    background: gradientString,
                    marginTop: `${hasData ? 0 : marginTop}`
                }}
            />
            {/* value labels & tooltips */}
            {PositionedLabels ? PositionedLabels : DefaultLabels}
        </Box>
    );
};

export default BarGradient;
